import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { UserRole } from '../types/context';

const AvatarMenu: React.FC = () => {
  const { user } = useUser();
  const clerk = useClerk();
  const navigate = useNavigate();
  const { userRole, roleToggleState, toggleUserRole } = useAppContext();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      // If click is inside the avatar container or inside the portal menu, do nothing
      if (ref.current && ref.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('click', onDoc);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Compute menu position when opening so we can render into body (avoids clipping by overflow:hidden parents)
  useEffect(() => {
    if (!open) {
      setMenuPos(null);
    }
  }, [open]);

  const handleSignOut = async () => {
    try {
      await clerk.signOut();
      navigate('/');
    } catch (err) {
      console.error('Sign out failed', err);
    }
  };

  const handleSignIn = () => {
    // Prefer client-side sign-in route; adapt if you use Clerk's widgets
    navigate('/sign-in');
  };

  const handleRoleSwitch = async (newRole: UserRole) => {
    if (!toggleUserRole) return;
    if (roleToggleState?.isToggling || newRole === userRole) return;
    try {
      await toggleUserRole(newRole);
      // Navigate to role-appropriate dashboard
      if (newRole === 'sponsor') navigate('/sponsor/dashboard');
      else navigate('/events');
      setOpen(false);
    } catch (err) {
      console.error('Failed to toggle role', err);
    }
  };

  const avatarUrl = user?.imageUrl;

  return (
    <div className="relative" ref={ref}>
      <button
        aria-haspopup="menu"
        aria-expanded={open}
        ref={btnRef}
        onClick={() => {
          if (btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            const menuWidth = 224; // w-56
            const left = Math.max(8, rect.right - menuWidth + window.scrollX);
            const top = rect.bottom + window.scrollY + 8; // small offset
            setMenuPos({ top, left, width: menuWidth });
          }
          setOpen(v => !v);
        }}
        className="w-10 h-10 rounded-xl overflow-hidden focus:outline-none focus:ring avatar-btn"
        title={user?.fullName || 'Account'}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-sm text-gray-700">{user?.firstName?.[0] || 'U'}</div>
        )}
      </button>

      {open && menuPos && createPortal(
        <div ref={menuRef} role="menu" style={{ position: 'absolute', top: menuPos.top, left: menuPos.left, width: menuPos.width, zIndex: 9999 }}>
          <div className="bg-white border rounded-md shadow-lg">
            <div className="py-2">
            {/* User header */}
            {user ? (
              <div className="px-4 pb-2 border-b">
                <div className="font-medium text-sm">{user.fullName || user.firstName || 'User'}</div>
                <div className="text-xs text-gray-500">{user.emailAddresses?.[0]?.emailAddress}</div>
              </div>
            ) : null}

            <div className="p-2">
              {/* Show role switch for all authenticated users */}
              {user && (
                <>
                  <div className="px-1">
                    <div className="text-xs text-gray-400 px-2 pb-1">Role</div>
                    <div className="flex flex-col">
                      <button
                        role="menuitem"
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded ${userRole === 'participant' ? 'font-semibold bg-gray-50' : ''}`}
                        onClick={() => handleRoleSwitch('participant')}
                        disabled={roleToggleState?.isToggling}
                      >
                        {roleToggleState?.isToggling && userRole !== 'participant' ? 'Switching...' : 'Hacker'}
                      </button>
                      <button
                        role="menuitem"
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded ${userRole === 'sponsor' ? 'font-semibold bg-gray-50' : ''}`}
                        onClick={() => handleRoleSwitch('sponsor')}
                        disabled={roleToggleState?.isToggling}
                      >
                        {roleToggleState?.isToggling && userRole !== 'sponsor' ? 'Switching...' : 'Host/Sponsor'}
                      </button>
                    </div>
                  </div>
                  <div className="border-t my-2" />
                </>
              )}

              {/* Profile / Auth actions */}
              {user ? (
                <>
                  <button
                    role="menuitem"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    onClick={handleSignOut}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <button
                  role="menuitem"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                  onClick={() => { handleSignIn(); setOpen(false); }}
                >
                  Sign in
                </button>
              )}
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AvatarMenu;
